import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface HeroSlide {
  workId: string;
  title: string;
  typeLabel: string;
  year: number;
  genre: string;
  runtime: string;
  tagline: string;
  image: string;
}

const slides: HeroSlide[] = [
  {
    workId: "w-20",
    title: "海边书店",
    typeLabel: "最新剧集",
    year: 2026,
    genre: "治愈 / 生活",
    runtime: "全 10 集",
    tagline: "一间即将关门的旧书店，一本从未归还的书。",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20an%20empty%20seaside%20railway%20platform%20at%20dusk%2C%20deep%20navy%20sky%20with%20warm%20amber%20horizon%20glow%2C%20fog%20and%20soft%20film%20grain%2C%20artistic%20minimal%20composition%2C%20no%20people%2C%20high%20detail%2C%20atmospheric&width=1600&height=900&seq=hero-01&orientation=landscape",
  },
  {
    workId: "w-14",
    title: "潮汐之间",
    typeLabel: "口碑剧集",
    year: 2021,
    genre: "悬疑 / 剧情",
    runtime: "全 8 集",
    tagline: "潮水退去时，岛上的旧事重新露了出来。",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20rainy%20neon-lit%20Tokyo%20backstreet%20at%20night%2C%20warm%20amber%20reflections%20on%20wet%20pavement%2C%20deep%20navy%20shadows%2C%20film%20grain%2C%20moody%20atmospheric%2C%20no%20people&width=1600&height=900&seq=hero-02&orientation=landscape",
  },
  {
    workId: "w-09",
    title: "远山的灯火",
    typeLabel: "获奖影片",
    year: 2023,
    genre: "家庭 / 剧情",
    runtime: "126 分钟",
    tagline: "只要灯还亮着，走夜路的人就不会迷路。",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20late%20summer%20countryside%20road%20with%20tall%20grass%20and%20warm%20sunset%20backlight%2C%20deep%20navy%20storm%20clouds%2C%20film%20grain%2C%20artistic%2C%20no%20people&width=1600&height=900&seq=hero-03&orientation=landscape",
  },
];

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const current = slides[active];

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((index) => (index + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [active]);

  const goTo = (index: number) => {
    setActive((index + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={slide.workId}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={`${slide.title} 焦点剧照`}
              title={`${slide.title} 焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/10 to-primary-950/40"></div>
      </div>

      <div className="relative mx-auto flex min-h-[540px] w-full max-w-[1280px] flex-col justify-between px-4 pb-10 pt-12 md:min-h-[620px] md:px-6 md:pb-14 md:pt-16">
        <div className="max-w-3xl animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1.5 text-[11px] font-medium tracking-wide text-accent-300">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-400"></span>
            影视作品档案 · 持续更新
          </span>

          <h1 className="mt-5 font-bold tracking-tight text-background-50">
            <span className="block text-[17px] font-medium tracking-[0.32em] text-background-200 md:text-xl">
              大桥未久
            </span>
            <span className="mt-2 block text-[30px] leading-[1.25] md:text-[46px] md:leading-[1.15]">
              影视<span className="text-accent-400">作品大全</span>
            </span>
            <span className="mt-1 block text-[22px] font-semibold text-background-100 md:text-[30px]">
              及最新资讯
            </span>
          </h1>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-[15px]">
            汇总电影与电视剧作品资料、演员信息与行业动态。按类型、年份快速筛选，
            查看剧情简介、演职员信息与相关推荐。
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/category/movie"
              className="inline-flex min-h-[48px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-film-line text-base"></i>
              浏览作品全集
            </Link>
            <Link
              to="/news"
              className="inline-flex min-h-[48px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-6 text-sm font-medium text-background-50 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
            >
              <i className="ri-newspaper-line text-base"></i>
              阅读最新资讯
            </Link>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-6 border-t border-background-50/20 pt-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="animate-fade-in max-w-xl" key={current.workId}>
            <p className="flex items-center gap-2 text-[11px] tracking-[0.2em] text-accent-400">
              <i className="ri-focus-3-line text-sm"></i>
              焦点推荐 · {current.typeLabel}
            </p>
            <Link
              to={`/detail/${current.workId}`}
              className="mt-2 block cursor-pointer text-xl font-semibold text-background-50 transition-colors hover:text-accent-400 md:text-2xl"
            >
              《{current.title}》
            </Link>
            <p className="mt-1.5 text-xs text-background-300 md:text-sm">
              <span className="font-label">{current.year}</span>
              <span className="mx-2 text-background-500">·</span>
              {current.genre}
              <span className="mx-2 text-background-500">·</span>
              {current.runtime}
            </p>
            <p className="mt-2 text-sm text-background-200">{current.tagline}</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="上一张焦点图"
              onClick={() => goTo(active - 1)}
              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide.workId}
                  type="button"
                  aria-label={`切换到 ${slide.title}`}
                  onClick={() => goTo(index)}
                  className={`h-1.5 cursor-pointer rounded-full transition-all ${
                    index === active
                      ? "w-8 bg-accent-400"
                      : "w-3 bg-background-50/40 hover:bg-background-50/70"
                  }`}
                ></button>
              ))}
            </div>
            <button
              type="button"
              aria-label="下一张焦点图"
              onClick={() => goTo(active + 1)}
              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}